import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { FormFields } from '~/components/FormFields';
import { GlobalEl } from '~/styles/app';
import notify from '~/components/Notify';
import Drawer from '~/components/Drawer';
import { useAuth } from '~/routes/components/AuthProvider';
import { Tab } from 'semantic-ui-react';
import { StyledTabPane, StyledMenuItem, DrawerFooter } from '../../style';
import { FeedSupplierService } from '~/api/services/feedSupplier';
import { handleErrors } from '~/utils/helpers';

interface Props {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  fetchData: () => void;
  idFeedSupplier?: number | null;
}

const CreateEditFeedSupplier: React.FC<Props> = (props) => {
  const { isVisible, handleHiddenDrawer, idFeedSupplier, fetchData } = props;
  const { t } = useTranslation();
  const { systemData: { languages } } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { handleSubmit, control, reset, formState } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (isVisible) {
      if (idFeedSupplier) {
        editFeedSupplier(idFeedSupplier);
      } else {
        reset({
          status: 1,
          languages: getLangs()
        });
      }
    }
  }, [isVisible, idFeedSupplier]);

  function getLangs() {
    return languages.map(language => ({
      id: language.id,
      name: ''
    }));
  }

  function getParsedLanguages(dataLangs) {
    const result = [...dataLangs];
    languages.forEach((language) => {
      const index = result.findIndex(lang => lang.id === language.id);
      if (index < 0) {
        result.push({
          id: language.id,
          name: '',
          order: language.order
        });
      } else {
        result[index].order = language.order;
      }
    });
    result.sort((a, b) => a.order - b.order);
    result.forEach(lang => delete lang.order);
    return result;
  }

  async function editFeedSupplier(id: number) {
    setIsLoading(true);
    try {
      const response = await FeedSupplierService.edit(id);
      response.data.languages = getParsedLanguages(response.data.languages);
      reset(response.data);
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  }

  async function onSubmit(data) {
    setIsLoading(true);
    try {
      if (!idFeedSupplier) {
        await FeedSupplierService.store(data);
        notify(t('Successfully created feed supplier'), 'success');
      } else {
        await FeedSupplierService.update(idFeedSupplier, data);
        notify(t('Successfully updated feed supplier'), 'success');
      }
      fetchData();
      handleHiddenDrawer();
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  }

  function errorTab() {
    //@ts-ignore
    return formState.errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number');
  }

  function getMenuItem(label: string, index: number) {
    const errors = errorTab() || [];

    return (
      <StyledMenuItem key={label} className='tab-item'>
        <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
      </StyledMenuItem>
    );
  }

  function renderLanguageFields() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        panes={languages.map((language) => {
          const indexLanguage = languages.findIndex(lang => lang.id === language.id);
          return {
            //@ts-ignore
            menuItem: getMenuItem(language.label, indexLanguage),
            pane: (
              <StyledTabPane key={language.id}>
                <FormFields.Input
                  control={control}
                  name={`languages[${indexLanguage}].name`}
                  label={t('Feed supplier name')}
                  placeholder={t('Feed supplier name')}
                  rules={{
                    required: {
                      value: true,
                      message: t('Please enter feed supplier name')
                    }
                  }}
                />
              </StyledTabPane>
            )
          };
        })}
      />
    );
  }

  function renderStatusField() {
    return (
      <FormFields.Checkbox
        control={control}
        name='status'
        label={t('Active')}
        checkboxValue={1}
        defaultValue={0}
      />
    );
  }

  const renderContent = () => {
    return (
      <>
        {renderStatusField()}
        {renderLanguageFields()}
        <DrawerFooter>
          <GlobalEl.PrimaryButton
            disabled={Object.keys(formState.dirtyFields).length === 0}
          >
            {idFeedSupplier ? t('Update') : t('Add')}
          </GlobalEl.PrimaryButton>
        </DrawerFooter>
      </>
    );
  };

  return (
    <Drawer
      isVisible={isVisible}
      handleSubmit={handleSubmit(onSubmit)}
      isBusy={isLoading}
      header={idFeedSupplier ? t('Edit feed supplier') : t('Create feed supplier')}
      closeOnEscape
      onHide={() => {
        reset();
        handleHiddenDrawer();
      }}
      content={renderContent()}
    />
  );
};

export default CreateEditFeedSupplier;
